import {format, formatInTimeZone} from 'date-fns-tz'
import {enUS, de, enCA, es, fr, frCA, it, ja, ko, nl, pt, ptBR, ru, zhCN, type Locale} from 'date-fns/locale'
import {type AppLanguageCode, DEFAULT_LANGUAGE} from '@/translations/i18n.tsx'
// import i18next from 'i18next' instead of '@/translations/i18n.tsx' to change formatLocaleDate lang without reloading the page
import i18next from 'i18next'
import {parseISO} from 'date-fns'

const locales = {
    en: enUS,
    es,
    de,
    fr,
    it,
    ja,
    ko,
    nl,
    pt,
    ru,
    zh: zhCN,
    es_419: es,
    es_MX: es,
    fr_CA: frCA,
    pt_BR: ptBR,
    en_CA: enCA
} as const satisfies Record<AppLanguageCode, Locale>

export const getLocale = (languageCode: string): Locale => {
    return locales[languageCode as keyof typeof locales] || locales[DEFAULT_LANGUAGE.code]
}

/**
 * Parses a date the way the app means it, which `new Date(string)` does not.
 *
 * ECMAScript reads a date-only string (`'2026-03-02'`) as **UTC** midnight but a
 * date-time one (`'2026-03-02T00:00:00'`) as **local** midnight. Calendar dates
 * here — an event's `date`, a roadmap's `dateFrom`, a filter's `dateTo` — carry
 * no zone and mean a day on the viewer's calendar, so reading them as UTC moves
 * them a day back for anyone west of it: `'2026-03-02'` becomes Sunday
 * March 1st, 16:00, in Los Angeles. `parseISO` reads them as local midnight,
 * which is what dayjs did and what every consumer here assumes.
 *
 * Full instants (`'…T…Z'` or with an offset) are unaffected: their zone is
 * stated, so both parsers agree, and `parseISO` keeps them absolute.
 */
export const parseDate = (value: string | Date): Date => (value instanceof Date ? value : parseISO(value))

export const formatLocaleDate = (date: string | Date, formatType = 'P') => {
    const currentLang = i18next.language || DEFAULT_LANGUAGE.code
    const locale = locales[currentLang as keyof typeof locales] || locales[DEFAULT_LANGUAGE.code]
    return format(parseDate(date), formatType, {locale})
}

export const getTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export const formatInTimezone = (date: string | Date, formatType = 'P') => {
    const timezone = getTimezone()
    return formatInTimeZone(parseDate(date), timezone, formatType, {
        locale: locales[i18next.language as keyof typeof locales] || locales[DEFAULT_LANGUAGE.code]
    })
}
